/*
 * Copyright © 2015-2019 Serenova, LLC. All rights reserved.
 */

/**
 *
 * IntegrationsDetailsPanel
 *
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { SidePanelTable, DetailHeader, LoadingSpinnerSVG, DetailsPanelMessage } from 'cx-ui-components';
import DetailWrapper from '../../../components/DetailWrapper';
import { detailHeaderText } from '../../../utils';
import { entitiesMetaData } from '../../../redux/modules/entities/metaData';
import { Fragment } from 'react';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const WrappedDetailHeader = styled(DetailHeader)`
  margin-left: 35px;
`;

const ConnectionStatusRow = styled.div`
  margin: 6px 24px 12px 35px;
`;

const ConnectionStateBadge = styled.span`
  display: inline-block;
  border-radius: 12px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => props.textColor};
  background-color: ${(props) => props.backgroundColor};
  text-transform: capitalize;
`;

const ConnectionStatusMessage = styled(DetailsPanelMessage)`
  margin: 4px 24px 16px 24px;
`;

export default class IntegrationsDetailsPanel extends Component {
  constructor(props) {
    super(props);
  }

  getIntegrationPropertyValue(name) {
    const { integrationProperties } = this.props;
    if (!integrationProperties) return undefined;
    if (typeof integrationProperties.get === 'function') {
      return integrationProperties.get(name);
    }
    return integrationProperties[name];
  }

  getSalesforceConnectionStatus() {
    const authVersion = this.getIntegrationPropertyValue('authVersion');
    const rawConnectionState = this.getIntegrationPropertyValue('connectionState');
    const hasV2Markers = rawConnectionState !== undefined;
    const isV2 =
      authVersion === 'v2-auth-code' ||
      ((authVersion === undefined || authVersion === null || authVersion === '') && hasV2Markers);

    if (!isV2) {
      return null;
    }

    // Show a default status while backend state is empty.
    if (rawConnectionState === undefined || rawConnectionState === null || rawConnectionState === '') {
      return { label: 'Not Connected', textColor: '#41464b', backgroundColor: '#e2e3e5', showReconnectHint: true };
    }

    switch (rawConnectionState) {
      case 'connected':
        return { label: 'Connected', textColor: '#0f5132', backgroundColor: '#d1e7dd', showReconnectHint: false };
      case 'reauth-required':
        return { label: 'Reauth Required', textColor: '#664d03', backgroundColor: '#fff3cd', showReconnectHint: true };
      case 'revoked':
        return { label: 'Revoked', textColor: '#842029', backgroundColor: '#f8d7da', showReconnectHint: true };
      default:
        return { label: 'Not Connected', textColor: '#41464b', backgroundColor: '#e2e3e5', showReconnectHint: true };
    }
  }

  componentDidMount() {
    if (this.props.integrationType === 'email') {
      this.props.setCurrentSubEntity('listeners');
    }
  }

  render() {
    const isSavedIntegration =
      this.props.selectedEntityId &&
      this.props.selectedEntityId !== 'create' &&
      this.props.selectedEntityId !== 'bulk';
    const salesforceConnectionStatus = this.getSalesforceConnectionStatus();

    return (
      <Fragment>
        {this.props.selectedIntegrationType === 'twilio' && this.props.integrationsFetching ? (
          <LoadingSpinnerSVG size={60}/>
        ) : (
          <Wrapper className="dtpanel-entity-integrations">
            {this.props.children}
            {isSavedIntegration && salesforceConnectionStatus && (
              <DetailWrapper open data-automation="salesforceConnectionStateSVG">
                <WrappedDetailHeader text={'Connection Status'} />
                <ConnectionStatusRow>
                  <ConnectionStateBadge
                    textColor={salesforceConnectionStatus.textColor}
                    backgroundColor={salesforceConnectionStatus.backgroundColor}
                    data-automation="salesforceConnectionStateBadge"
                  >
                    {salesforceConnectionStatus.label}
                  </ConnectionStateBadge>
                </ConnectionStatusRow>
                {salesforceConnectionStatus.showReconnectHint && (
                  <ConnectionStatusMessage
                    text={'Use Connect to Salesforce in Properties to reconnect.'}
                    type={'warning'}
                    data-automation="salesforceReconnectHint"
                  />
                )}
              </DetailWrapper>
            )}
            {(this.props.integrationType === 'facebook' || this.props.integrationType === 'email' || this.props.integrationType === 'salesforce') && (
              <DetailWrapper open data-automation="integrationListenersSVG">
                <WrappedDetailHeader
                  userHasUpdatePermission={!this.props.integrationsFetching && !this.props.inherited && this.props.userHasUpdatePermission}
                  text={detailHeaderText(this.props.listenersItems, 'Listener(s)')}
                  onActionButtonClick={() => this.props.setSelectedSubEntityId('listeners')}
                />
                <SidePanelTable
                  tableType={'sidePanel'}
                  contains="listeners"
                  userHasUpdatePermission={this.props.userHasUpdatePermission}
                  updateSubEntity={(listItemId, row, subEntityName) => this.props.setSelectedSubEntityId(listItemId)}
                  items={this.props.listenersItems}
                  fields={this.props.listenersFields}
                  fetching={this.props.integrationsFetching && !this.props.listenersItems.length}
                  itemApiPending={this.props.itemApiPending}
                />
              </DetailWrapper>
            )}
            {this.props.integrationType === 'twilio' && (
              <DetailWrapper open data-automation="twilioGlobalDialParamsSVG">
                <WrappedDetailHeader
                  userHasUpdatePermission={
                    !this.props.integrationsFetching &&
                    !this.props.inherited &&
                    this.props.userHasUpdatePermission &&
                    this.props.sidePanelUpdatePermissions.twilioGlobalDialParams
                  }
                  text={detailHeaderText(this.props.globalDialParamsItems, 'Global Dial Parameters')}
                  onActionButtonClick={() => this.props.setSelectedSubEntityId('twilioGlobalDialParams')}
                />
                <SidePanelTable
                  tableType={'sidePanel'}
                  userHasUpdatePermission={this.props.userHasUpdatePermission && this.props.sidePanelUpdatePermissions.twilioGlobalDialParams}
                  deleteSubEntity={(item) => this.props.removeTwilioGlobalDialParam(item)}
                  updateSubEntity={(item) => this.props.updateTwilioGlobalDialParam(item)}
                  inherited={this.props.inherited}
                  items={this.props.globalDialParamsItems}
                  fields={entitiesMetaData.integrations.membersTableFields.globalDialParams}
                  fetching={this.props.integrationsFetching && !this.props.globalDialParamsItems.length}
                  itemApiPending={this.props.itemApiPending}
                />
              </DetailWrapper>
            )}
            {/* HIDDEN as part of CXV1-31311
            {this.props.integrationType === 'ecg' && (
              <DetailWrapper open data-automation="ecgGlobalDialParamsSVG">
                <WrappedDetailHeader
                  userHasUpdatePermission={
                    !this.props.integrationsFetching &&
                    !this.props.inherited &&
                    this.props.userHasUpdatePermission &&
                    this.props.sidePanelUpdatePermissions.ecgGlobalDialParams
                  }
                  text={detailHeaderText(this.props.globalDialParamsItems, 'Global Dial Parameters')}
                  onActionButtonClick={() => this.props.setSelectedSubEntityId('ecgGlobalDialParams')}
                />
                <SidePanelTable
                  tableType={'sidePanel'}
                  userHasUpdatePermission={this.props.userHasUpdatePermission && this.props.sidePanelUpdatePermissions.ecgGlobalDialParams}
                  deleteSubEntity={(item) => this.props.removeEcgGlobalDialParam(item)}
                  updateSubEntity={(item) => this.props.updateEcgGlobalDialParam(item)}
                  inherited={this.props.inherited}
                  items={this.props.globalDialParamsItems}
                  fields={entitiesMetaData.integrations.membersTableFields.globalDialParams}
                  fetching={this.props.integrationsFetching && !this.props.globalDialParamsItems.length}
                  itemApiPending={this.props.itemApiPending}
                />
              </DetailWrapper>
            )}*/
            }
          </Wrapper>
        )}
      </Fragment>
    );
  }
}

IntegrationsDetailsPanel.propTypes = {
  children: PropTypes.any,
  globalDialParamsItems: PropTypes.array,
  inherited: PropTypes.bool,
  integrationType: PropTypes.string,
  integrationProperties: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
  currentFormValues: PropTypes.object,
  integrationsFetching: PropTypes.bool,
  itemApiPending: PropTypes.string,
  listenersFields: PropTypes.array,
  listenersItems: PropTypes.array,
  removeTwilioGlobalDialParam: PropTypes.func,
  selectedEntityId: PropTypes.string,
  selectedIntegrationType: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  setCurrentSubEntity: PropTypes.func,
  setSelectedSubEntityId: PropTypes.func,
  sidePanelUpdatePermissions: PropTypes.object,
  updateTwilioGlobalDialParam: PropTypes.func,
  userHasUpdatePermission: PropTypes.bool,
  userHasViewPermission: PropTypes.bool,
};
